import React, { useEffect, useState } from 'react';
import VideoPlayer from './videoPlayer';
import VideoPlayerItem from './videoItem';
import VideoCounter from './videoCounter';
import ToggleBtn from './videoToggleBtn';
import VideoControls from './videoControls';
import MovingImageProps from './properties/movingImageProps';
import VideoScrollbar from './videoScrollbar';
import Video from './videoModule';
import VideoPlaylistLabels from './properties/videoPlaylistLabels';
import VideoPlaylistProps from './properties/videoPlaylistProps';

interface VideoJsonConfig {
  jsonConfig: string;
}

export const generateUniqueId = (prefix: string): string => {
  return `${prefix}_${Math.random().toString(36).substr(2, 9)}`;
};

const formatDuration = (minutes: string, seconds: string) => {
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const parseVideoProps = (videoPlaylistProps: string) => {
  const props: VideoPlaylistProps = JSON.parse(videoPlaylistProps);
  return props;
};

const VideoPlaylist = (props: VideoJsonConfig) => {
  const { jsonConfig } = props;
  const videoPlaylistConfig = jsonConfig ? parseVideoProps(jsonConfig) : null;
  const videoConfig = videoPlaylistConfig ? videoPlaylistConfig.videos : [];
  const movingImage = videoPlaylistConfig ? videoPlaylistConfig.movingImage : ({} as MovingImageProps);
  const labels = videoPlaylistConfig ? videoPlaylistConfig.labels : ({} as VideoPlaylistLabels);

  const [currentVideoConfig, setCurrentVideoConfig] = useState(videoConfig[0]);
  const [currentVideoId, setCurrentVideoId] = useState(videoConfig[0].videoId);
  const [currentVideoPosition, setCurrentVideoPosition] = useState(1);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [currentScrollIndex, setCurrentScrollIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [playTrigger, setPlayTrigger] = useState(0);

  const containerId = generateUniqueId('videoPlaylist');
  const preSetScrollIndex = (nextIndex: number) => {
    setCurrentScrollIndex(nextIndex);
  };

  const scollUp = () => {
    preSetScrollIndex(currentScrollIndex - 1);
  };

  const scollDown = () => {
    preSetScrollIndex(currentScrollIndex + 1);
  };
  const playVideo = (index: number) => {
    if (currentVideoIndex === index) {
      // set random number to trigger a change
      setPlayTrigger(Math.random());
    } else {
      const newVideoConfig = videoConfig[index];
      if (newVideoConfig) {
        setCurrentVideoId(newVideoConfig.videoId);
        setCurrentVideoConfig(newVideoConfig);
        setCurrentVideoPosition(index + 1);
        setCurrentVideoIndex(index);
        showVideoCover(newVideoConfig.thumbnail);
      }
    }
  };

  const prePlayVideo = (index: number) => {
    // Uses LazyLoading? Video-element is not initialized yet. Trigger click on cover to activate video first.
    const video = document.querySelector(`#${containerId} video`);
    if (!video) {
      const lazyLoadingElement = document.querySelector(`#${containerId} .mi-cover`) as HTMLElement;
      if (lazyLoadingElement) {
        lazyLoadingElement.click();
        window.setTimeout(() => {
          playVideo(index);
        }, 50);
      }
      return;
    }
    playVideo(index);
  };
  const showVideoCover = (thumbnail: string) => {
    const video = document.querySelector(`#${containerId} video`);
    if (!video) {
      const videoCoverContainer = document.querySelector(`#${containerId} .mi-cover`) as HTMLElement;
      if (videoCoverContainer) {
        if(thumbnail){
          videoCoverContainer.style.backgroundImage = `url(${thumbnail})`;
        }
      }
    }
  };
  const toggleAutoPlay = () => {
    setAutoPlay(!autoPlay);
  };
  const playPrevVideo = (force?: boolean) => {
    if (autoPlay || force) {
      prePlayVideo(currentVideoIndex - 1);
      preSetScrollIndex(currentVideoIndex - 1);
    }
  };

  const playNextVideo = (event: Event | null, force?: boolean) => {
    if (autoPlay || force) {
      const nextIndex = currentVideoIndex + 1;
      prePlayVideo(nextIndex);
      if ((nextIndex >= 3 && nextIndex < videoConfig.length) || force) {
        preSetScrollIndex(nextIndex - 2);
      }
    }
  };

  const onFocusItem = (e: Event) => {
    const itemIndex = Array.from(document.querySelectorAll('.video-playlist__item')).indexOf(e.target as Element);

    if (
      itemIndex < videoConfig.length - 2 &&
      (currentScrollIndex - itemIndex <= -3 || currentScrollIndex - itemIndex >= 1)
    ) {
      window.setTimeout(() => {
        // prevent focus event from killing click
        preSetScrollIndex(itemIndex);
      }, 100);
    }
  };

  const trackVideo = (action: string) => {
    const { wt } = window as any;
    if (wt && wt.sendinfo) {
      wt.sendinfo({ linkId: action });
    }
  };

  useEffect(() => {
    document.querySelectorAll('.video-playlist__item').forEach((el) => {
      el.addEventListener('focusin', onFocusItem);
    });

    return () => {
      document.querySelectorAll('.video-playlist__item').forEach((el) => {
        el.removeEventListener('focusin', onFocusItem);
      });
    };
  }, [currentScrollIndex]);

  return (
    <div className="video-playlist" id={containerId}>
      <VideoPlayer
        videoConfig={currentVideoConfig}
        onNext={() => {
          trackVideo(`rechts ${currentVideoId}`);
          playNextVideo(null, true);
        }}
        onPrev={() => {
          trackVideo(`links ${currentVideoId}`);
          playPrevVideo(true);
        }}
        video={
          <Video
            videoId={currentVideoConfig.videoId}
            movingImage={movingImage}
            onVideoEnded={playNextVideo}
            playTrigger={playTrigger}
          />
        }
        allowNext={currentVideoIndex < videoConfig.length - 1}
        allowPrev={currentVideoIndex > 0}
      />

      <div className="video-playlist__overview">
        <div className="video-playlist__top">
          <VideoCounter current={currentVideoPosition} total={videoConfig.length} labels={labels} />
          <ToggleBtn active={autoPlay} onToggle={toggleAutoPlay} labels={labels} />
        </div>
        <div className={`video-playlist__list scroll-index-${currentScrollIndex}`}>
          <div className="video-playlist__list-inner">
            {videoConfig.map((video, index) => (
              <VideoPlayerItem
                key={video.videoId}
                videoConfig={video}
                isCurrent={index === currentVideoIndex}
                order={index}
                title={currentVideoConfig.headline}
                labels={labels}
                onPlay={prePlayVideo}
                position={index}
                isReachable={index >= currentScrollIndex && index < currentScrollIndex+3}
                videoController={
                  <VideoControls
                    // videoId={video.videoId}
                    onPlay={prePlayVideo}
                    position={index}
                    // title={currentVideoConfig.headline}
                    duration={formatDuration(video.minutes, video.seconds)}
                    labels={labels}
                  />
                }
              />
            ))}
          </div>
          <VideoScrollbar
            labels={labels}
            onUp={scollUp}
            onDown={scollDown}
            scrollIndex={currentScrollIndex}
            maxScrollIndex={videoConfig.length - 3}
          />
        </div>
      </div>
    </div>
  );
};

export default VideoPlaylist;
